import {
  Application,
  AuthorisedUser,
  AxeStatus,
  AxeSubscriptionIdentifier,
  CalculatedValueType,
  CollateralDetailsSubscriptionIdentifier,
  DealEvent,
  DealHistorySubscriptionIdentifier,
  DealStatus,
  DealSubscriptionIdentifier,
  FilterOperation,
  Market,
  NegotiationHistorySubscriptionIdentifier,
  NegotiationStatus,
  NegotiationSubscriptionIdentifier,
  OrderSubscriptionIdentifier,
  RepoClassification,
  ServicingType,
  SpreadsheetUploadIdentifier,
  SubscriptionFilter,
  SubscriptionIdentifier,
  User
} from 'api'
import { createObjectCache, toLocalDateString } from 'utils'
import { getEnumValues } from './enumUtils'
import { isActive } from './orderUtils'
import { GlobalGridSettings } from '../store/grid/gridTypes'
import { isEqual } from 'lodash-es'
import { endOfDay } from 'date-fns'

// Filter Values

const DealHistoryLastEvents = [
  DealEvent.SERVICING_SEND,
  DealEvent.SERVICING_ACCEPT,
  DealEvent.SERVICING_WITHDRAW,
  DealEvent.SERVICING_REJECT
]
const OpenRepoStructuredAndRollStatuses = getEnumValues(DealStatus).filter(
  status =>
    ![DealStatus.CANCELLED, DealStatus.UNASSIGNED, DealStatus.ASSIGNED, DealStatus.UNALLOCATED, DealStatus.ALLOCATED].includes(status)
)
const MidTradeEventStatuses = [DealStatus.SERVICING_WITH_RESPONDER, DealStatus.SERVICING_WITH_INITIATOR]
const MidTradeEventServicingTypes = [
  ServicingType.RERATE,
  ServicingType.CLOSE_OPEN,
  ServicingType.SUBSTITUTION,
  ServicingType.TERMINATION,
  ServicingType.RETURN_RECALL,
  ServicingType.ROLL_IN_PLACE,
  ServicingType.SERVICING_TRADER_AMEND,
  ServicingType.SERVICING_TRADER_CANCEL,
  ServicingType.SERVICING_ADMIN_CANCEL
]
const InitiatedNegotiationStatuses = [
  NegotiationStatus.ITEMS_IN_DIFFERENT_STATES,
  NegotiationStatus.REQUESTED_WITHOUT_PRICE,
  NegotiationStatus.REQUESTED_WITH_PRICE,
  NegotiationStatus.QUOTED,
  NegotiationStatus.QUOTED_SUBJECT,
  NegotiationStatus.WITHDRAWN
]
const InitiatedNegotiationExpiredRejectedStatuses = [
  NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED,
  NegotiationStatus.EXPIRED,
  NegotiationStatus.REJECTED
]
const IncomingNegotiationStatuses = [
  NegotiationStatus.ITEMS_IN_DIFFERENT_STATES,
  NegotiationStatus.REQUESTED_WITHOUT_PRICE,
  NegotiationStatus.REQUESTED_WITH_PRICE,
  NegotiationStatus.QUOTED,
  NegotiationStatus.QUOTED_SUBJECT,
  NegotiationStatus.WITHDRAWN
]
const TerminatedNegotiationStatuses = getEnumValues(NegotiationStatus).filter(status => !isActive(status))
const TerminatedNegotiationNotificationStatuses = [
  NegotiationStatus.ACCEPTED,
  NegotiationStatus.REJECTED,
  NegotiationStatus.CANCELLED,
  NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED,
  NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_TERMINATED
]

/**
 * placeholder to include marketTradeDate in DefaultFilters when needed,
 * back end will fill the value field since we are telling it how to fill
 * it via the calculcatedValueType enum property
 **/
export const DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END: SubscriptionFilter = {
  fieldName: 'marketTradeDate',
  value: null,
  filterOperation: FilterOperation.EQUALS,
  calculatedValueType: CalculatedValueType.MARKET_TRADE_DATE_EUROPE,
  andFilter: {
    fieldName: 'market',
    filterOperation: FilterOperation.EQUALS,
    calculatedValueType: null,
    value: Market.EUROPE,
    orFilter: null,
    andFilter: null
  },
  orFilter: {
    fieldName: 'marketTradeDate',
    value: null,
    filterOperation: FilterOperation.EQUALS,
    calculatedValueType: CalculatedValueType.MARKET_TRADE_DATE_US,
    orFilter: null,
    andFilter: {
      fieldName: 'market',
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      value: Market.US,
      orFilter: null,
      andFilter: null
    }
  }
}

// Persistent Filters (required at all times)

export const PersistentSubscriptionFilters: Record<SubscriptionIdentifier, SubscriptionFilter[]> = {
  [AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER]: [],
  [AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR]: [],
  [DealSubscriptionIdentifier.BLOTTER]: [],
  [CollateralDetailsSubscriptionIdentifier.COLLATERAL_DETAILS]: [],
  [DealSubscriptionIdentifier.ROLLS]: [
    {
      fieldName: 'repoClassification',
      value: RepoClassification.FIXED,
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'status',
      value: OpenRepoStructuredAndRollStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'collateralId',
      value: null,
      filterOperation: FilterOperation.NOT_EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'committedServicingType',
      value: [
        ServicingType.CLOSE_OPEN,
        ServicingType.SUBSTITUTION,
        ServicingType.TERMINATION,
        ServicingType.RETURN_RECALL,
        ServicingType.ROLL
      ].join(','),
      filterOperation: FilterOperation.NOT_CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  ],
  [SpreadsheetUploadIdentifier.MTE_UPLOAD]: [],
  [DealSubscriptionIdentifier.STRUCTURED]: [
    {
      fieldName: 'repoClassification',
      value: RepoClassification.OPEN_EVERGREEN,
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'status',
      value: OpenRepoStructuredAndRollStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  ],
  [DealSubscriptionIdentifier.OPEN_REPO]: [
    {
      fieldName: 'repoClassification',
      value: RepoClassification.OPEN,
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'status',
      value: OpenRepoStructuredAndRollStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  ],
  // If this filter is changed review whether it needs to go to the DB.  At the moment it does not as
  // any live mid trade event will be in the cache.  [DealStatus.SERVICING_WITH_RESPONDER, DealStatus.SERVICING_WITH_INITIATOR]
  // See - com.weareadaptive.brokertec.gateway.websocket.messagehandlers.client.subscriptions.SubscriptionHelper.CACHE_ONLY_QUERIES
  [DealSubscriptionIdentifier.MID_TRADE_EVENT]: [
    {
      fieldName: 'status',
      value: MidTradeEventStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'servicingType',
      value: MidTradeEventServicingTypes.join(','),
      filterOperation: FilterOperation.CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  ],
  [DealSubscriptionIdentifier.DEAL_INTEREST_PERIOD]: [],
  [DealSubscriptionIdentifier.DEAL_HISTORY]: [],
  [DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: [
    {
      fieldName: 'servicingMarketTradeDate',
      value: null,
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: CalculatedValueType.MARKET_TRADE_DATE_EUROPE,
      andFilter: {
        fieldName: 'market',
        filterOperation: FilterOperation.EQUALS,
        calculatedValueType: null,
        value: Market.EUROPE,
        orFilter: null,
        andFilter: null
      },
      orFilter: {
        fieldName: 'servicingMarketTradeDate',
        value: null,
        filterOperation: FilterOperation.EQUALS,
        calculatedValueType: CalculatedValueType.MARKET_TRADE_DATE_US,
        orFilter: null,
        andFilter: {
          fieldName: 'market',
          filterOperation: FilterOperation.EQUALS,
          calculatedValueType: null,
          value: Market.US,
          orFilter: null,
          andFilter: null
        }
      }
    },
    {
      fieldName: 'lastEvent',
      value: DealHistoryLastEvents.join(','),
      filterOperation: FilterOperation.CONTAINS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    {
      fieldName: 'lastUpdatedAt',
      value: toLocalDateString(new Date()),
      filterOperation: FilterOperation.AFTER,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  [NegotiationSubscriptionIdentifier.INITIATED]: [
    {
      fieldName: 'status',
      value: InitiatedNegotiationStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: {
        fieldName: 'status',
        value: InitiatedNegotiationExpiredRejectedStatuses.join(','),
        filterOperation: FilterOperation.CONTAINS,
        calculatedValueType: null,
        orFilter: null,
        andFilter: {
          fieldName: 'canSendBackToStaging',
          value: true,
          filterOperation: FilterOperation.EQUALS,
          orFilter: null,
          andFilter: null,
          calculatedValueType: null
        }
      },
      andFilter: null
    },
    {
      fieldName: 'sentBackToStaging',
      value: false,
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  [NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW]: [],
  [NegotiationSubscriptionIdentifier.INCOMING]: [
    {
      fieldName: 'status',
      value: IncomingNegotiationStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  // If this filter is changed review whether it needs to go to the DB.  At the moment it does not as
  // created at is after today (these records are guaranteed to be in the cache.
  // See - com.weareadaptive.brokertec.gateway.websocket.messagehandlers.client.subscriptions.SubscriptionHelper.CACHE_ONLY_QUERIES
  [NegotiationSubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: [
    {
      fieldName: 'status',
      value: TerminatedNegotiationNotificationStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  // If this filter is changed review whether it needs to go to the DB.  At the moment it does not as
  // created at is after today (these records are guaranteed to be in the cache.
  // See - com.weareadaptive.brokertec.gateway.websocket.messagehandlers.client.subscriptions.SubscriptionHelper.CACHE_ONLY_QUERIES
  [NegotiationSubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: [
    {
      fieldName: 'status',
      value: TerminatedNegotiationNotificationStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  [NegotiationHistorySubscriptionIdentifier.HISTORICAL]: [
    {
      fieldName: 'status',
      value: TerminatedNegotiationStatuses.join(','),
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    },
    {
      fieldName: 'latest',
      value: true,
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  ],
  [NegotiationHistorySubscriptionIdentifier.LATEST]: [
    {
      fieldName: 'latest',
      value: true,
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    }
  ],
  [NegotiationHistorySubscriptionIdentifier.HISTORY]: [],
  [OrderSubscriptionIdentifier.STAGING]: []
}

// Default Filters (on initial application load)

export const DefaultDealHistorySubscriptionFilters: Record<DealHistorySubscriptionIdentifier, SubscriptionFilter[]> = {
  [DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: PersistentSubscriptionFilters.HISTORICAL_MID_TRADE_EVENTS
}

export const DefaultDealSubscriptionFilters: Record<DealSubscriptionIdentifier, SubscriptionFilter[]> = {
  [DealSubscriptionIdentifier.BLOTTER]: [DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END],
  [DealSubscriptionIdentifier.ROLLS]: [
    {
      fieldName: 'endDate',
      value: toLocalDateString(new Date()),
      filterOperation: FilterOperation.EQUALS,
      orFilter: null,
      andFilter: null,
      calculatedValueType: null
    },
    ...PersistentSubscriptionFilters.ROLLS
  ],
  [DealSubscriptionIdentifier.OPEN_REPO]: PersistentSubscriptionFilters.OPEN_REPO,
  [DealSubscriptionIdentifier.STRUCTURED]: PersistentSubscriptionFilters.STRUCTURED,
  [DealSubscriptionIdentifier.MID_TRADE_EVENT]: PersistentSubscriptionFilters.MID_TRADE_EVENT,
  [DealSubscriptionIdentifier.DEAL_INTEREST_PERIOD]: PersistentSubscriptionFilters.DEAL_INTEREST_PERIOD,
  [DealSubscriptionIdentifier.DEAL_HISTORY]: PersistentSubscriptionFilters.DEAL_HISTORY
}

export const DefaultNegotiationSubscriptionFilters: Record<NegotiationSubscriptionIdentifier, SubscriptionFilter[]> = {
  [NegotiationSubscriptionIdentifier.INITIATED]: [
    ...PersistentSubscriptionFilters.INITIATED,
    DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
  ],
  [NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW]: [],
  [NegotiationSubscriptionIdentifier.INCOMING]: [
    ...PersistentSubscriptionFilters.INCOMING,
    DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
  ],
  [NegotiationSubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: [
    ...PersistentSubscriptionFilters.INITIATED_TERMINATED_NOTIFICATION,
    DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
  ],
  [NegotiationSubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: [
    ...PersistentSubscriptionFilters.INCOMING_TERMINATED_NOTIFICATION,
    DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
  ]
}

export const DefaultNegotiationHistorySubscriptionFilters: Record<NegotiationHistorySubscriptionIdentifier, SubscriptionFilter[]> = {
  [NegotiationHistorySubscriptionIdentifier.LATEST]: [
    ...PersistentSubscriptionFilters.LATEST,
    DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
  ],
  [NegotiationHistorySubscriptionIdentifier.HISTORICAL]: [
    ...PersistentSubscriptionFilters.HISTORICAL,
    DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
  ],
  [NegotiationHistorySubscriptionIdentifier.HISTORY]: []
}

export const DefaultOrderSubscriptionFilters: Record<OrderSubscriptionIdentifier, SubscriptionFilter[]> = {
  [OrderSubscriptionIdentifier.STAGING]: [...PersistentSubscriptionFilters.STAGING, DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END]
}

export const DefaultSpreadsheetUploadFilters: Record<SpreadsheetUploadIdentifier, SubscriptionFilter[]> = {
  [SpreadsheetUploadIdentifier.MTE_UPLOAD]: []
}

export const DefaultAxeSubscriptionFilters: Record<AxeSubscriptionIdentifier, SubscriptionFilter[]> = createObjectCache(
  () => ({
    [AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER]: [
      {
        fieldName: 'status',
        value: AxeStatus.SUBMITTED,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      },
      DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
    ],
    [AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR]: [
      {
        fieldName: 'status',
        value: [AxeStatus.SUBMITTED, AxeStatus.DRAFT, AxeStatus.READY].join(','),
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      },
      DEFAULT_MARKET_TRADE_DATE_FILTER_TO_FILL_BY_BACK_END
    ]
  }),
  () => endOfDay(new Date()).getTime()
)

export const DefaultCollateralDetailsSubscriptionFilters: Record<CollateralDetailsSubscriptionIdentifier, SubscriptionFilter[]> = {
  [CollateralDetailsSubscriptionIdentifier.COLLATERAL_DETAILS]: []
}
export const DefaultSubscriptionFilters: Record<SubscriptionIdentifier, SubscriptionFilter[]> = createObjectCache(
  () => ({
    ...DefaultOrderSubscriptionFilters,
    ...DefaultAxeSubscriptionFilters,
    ...DefaultDealSubscriptionFilters,
    ...DefaultNegotiationSubscriptionFilters,
    ...DefaultNegotiationHistorySubscriptionFilters,
    ...DefaultDealHistorySubscriptionFilters,
    ...DefaultSpreadsheetUploadFilters,
    ...DefaultCollateralDetailsSubscriptionFilters
  }),
  () => endOfDay(new Date()).getTime()
)

export const PostTransformSubscriptionFilters: Record<SubscriptionIdentifier, SubscriptionFilter[]> = {
  [DealSubscriptionIdentifier.BLOTTER]: [],
  [DealSubscriptionIdentifier.ROLLS]: [],
  [DealSubscriptionIdentifier.OPEN_REPO]: [],
  [DealSubscriptionIdentifier.STRUCTURED]: [],
  [DealSubscriptionIdentifier.MID_TRADE_EVENT]: [],
  [SpreadsheetUploadIdentifier.MTE_UPLOAD]: [],
  [DealSubscriptionIdentifier.DEAL_INTEREST_PERIOD]: [],
  [DealSubscriptionIdentifier.DEAL_HISTORY]: [],
  [DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: [],
  [NegotiationSubscriptionIdentifier.INITIATED]: [
    {
      fieldName: 'negotiationSide',
      value: 'INITIATOR',
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  [NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW]: [
    {
      fieldName: 'negotiationSide',
      value: 'INITIATOR',
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  [NegotiationSubscriptionIdentifier.INCOMING]: [
    {
      fieldName: 'negotiationSide',
      value: 'RESPONDER',
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  ],
  [NegotiationSubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: [],
  [NegotiationSubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: [],
  [NegotiationHistorySubscriptionIdentifier.HISTORICAL]: [],
  [NegotiationHistorySubscriptionIdentifier.LATEST]: [],
  [NegotiationHistorySubscriptionIdentifier.HISTORY]: [],
  [OrderSubscriptionIdentifier.STAGING]: [],
  [AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER]: [],
  [AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR]: [],
  [CollateralDetailsSubscriptionIdentifier.COLLATERAL_DETAILS]: []
}

export type EntitySubscriptionFilters =
  | Record<OrderSubscriptionIdentifier, SubscriptionFilter[]>
  | Record<DealSubscriptionIdentifier, SubscriptionFilter[]>
  | Record<NegotiationSubscriptionIdentifier, SubscriptionFilter[]>
  | Record<NegotiationHistorySubscriptionIdentifier, SubscriptionFilter[]>
  | Record<DealHistorySubscriptionIdentifier, SubscriptionFilter[]>
  | Record<AxeSubscriptionIdentifier, SubscriptionFilter[]>

// State Filters (dependent on the store)

enum StateFilterId {
  HIDE_ORG_ACTIVITY = 'HIDE_ORG_ACTIVITY',
  INITIATED_SHOW_EXPIRED_REJECTED = 'INITIATED_SHOW_EXPIRED_REJECTED',
  OPEN_REPO_NULL_END_DATE = 'OPEN_REPO_NULL_END_DATE',
  SHOW_ONLY_USERS_SALES_ACTIVITY = 'SHOW_ONLY_USERS_SALES_ACTIVITY',
  SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION = 'SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION',
  AXE_SHOW_CANCELLED_INITIATOR = 'AXE_SHOW_CANCELLED_INITIATOR',
  AXE_SHOW_CANCELLED_RESPONDER = 'AXE_SHOW_CANCELLED_RESPONDER'
}

export const createStateSubscriptionFilters = ({
  authorisedUser
}: {
  authorisedUser: User
}): Record<StateFilterId, SubscriptionFilter> => ({
  [StateFilterId.HIDE_ORG_ACTIVITY]: {
    fieldName: 'createdBy',
    value: authorisedUser.username,
    calculatedValueType: null,
    filterOperation: FilterOperation.EQUALS,
    orFilter: null,
    andFilter: null
  },
  [StateFilterId.AXE_SHOW_CANCELLED_RESPONDER]: {
    fieldName: 'status',
    filterOperation: FilterOperation.CONTAINS,
    calculatedValueType: null,
    value: 'CANCELLED',
    andFilter: {
      andFilter: null,
      fieldName: 'previousStatus',
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      value: 'SUBMITTED'
    },
    orFilter: {
      andFilter: null,
      fieldName: 'status',
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      value: 'SUBMITTED'
    }
  },
  [StateFilterId.AXE_SHOW_CANCELLED_INITIATOR]: {
    fieldName: 'status',
    value: [AxeStatus.SUBMITTED, AxeStatus.DRAFT, AxeStatus.READY].join(','),
    filterOperation: FilterOperation.CONTAINS,
    calculatedValueType: null,
    orFilter: {
      fieldName: 'status',
      value: AxeStatus.CANCELLED,
      filterOperation: FilterOperation.CONTAINS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    },
    andFilter: null
  },
  [StateFilterId.INITIATED_SHOW_EXPIRED_REJECTED]: {
    fieldName: 'status',
    value: InitiatedNegotiationExpiredRejectedStatuses.join(','),
    filterOperation: FilterOperation.CONTAINS,
    calculatedValueType: null,
    orFilter: null,
    andFilter: {
      fieldName: 'canSendBackToStaging',
      value: true,
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    }
  },
  [StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY]: {
    fieldName: 'counterpartyPrimaryCoverUserId',
    value: authorisedUser.id,
    filterOperation: FilterOperation.EQUALS,
    calculatedValueType: null,
    orFilter: {
      fieldName: 'counterpartySecondaryCoverUserId',
      value: authorisedUser.id,
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: null,
      andFilter: null
    },
    andFilter: null
  },
  [StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION]: {
    fieldName: 'counterpartyPrimaryCoverUserId',
    value: authorisedUser.id,
    filterOperation: FilterOperation.EQUALS,
    calculatedValueType: null,
    orFilter: {
      fieldName: 'counterpartySecondaryCoverUserId',
      value: authorisedUser.id,
      filterOperation: FilterOperation.EQUALS,
      calculatedValueType: null,
      orFilter: {
        fieldName: 'childSalesCoverIds',
        value: authorisedUser.id,
        filterOperation: FilterOperation.CONTAINS,
        calculatedValueType: null,
        orFilter: null,
        andFilter: null
      },
      andFilter: null
    },
    andFilter: null
  },
  [StateFilterId.OPEN_REPO_NULL_END_DATE]: {
    fieldName: 'endDate',
    value: null,
    filterOperation: FilterOperation.EQUALS,
    calculatedValueType: null,
    orFilter: null,
    andFilter: null
  }
})

const ValidStateSubscriptionFilterIds: Record<SubscriptionIdentifier, StateFilterId[]> = {
  [SubscriptionIdentifier.INITIATED]: [StateFilterId.HIDE_ORG_ACTIVITY, StateFilterId.INITIATED_SHOW_EXPIRED_REJECTED],
  [SubscriptionIdentifier.INITIATED_GRID_VIEW]: [],
  [SubscriptionIdentifier.INCOMING]: [StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION],
  [SubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: [StateFilterId.HIDE_ORG_ACTIVITY],
  [SubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: [StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY],
  [SubscriptionIdentifier.HISTORICAL]: [StateFilterId.HIDE_ORG_ACTIVITY],
  [SubscriptionIdentifier.LATEST]: [StateFilterId.HIDE_ORG_ACTIVITY],
  [SubscriptionIdentifier.HISTORY]: [],
  [SubscriptionIdentifier.STAGING]: [],
  [SubscriptionIdentifier.BLOTTER]: [StateFilterId.HIDE_ORG_ACTIVITY, StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY],
  [SubscriptionIdentifier.ROLLS]: [StateFilterId.HIDE_ORG_ACTIVITY, StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY],
  [SubscriptionIdentifier.OPEN_REPO]: [
    StateFilterId.OPEN_REPO_NULL_END_DATE,
    StateFilterId.HIDE_ORG_ACTIVITY,
    StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY
  ],
  [SubscriptionIdentifier.STRUCTURED]: [
    StateFilterId.OPEN_REPO_NULL_END_DATE,
    StateFilterId.HIDE_ORG_ACTIVITY,
    StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY
  ],
  [SubscriptionIdentifier.MID_TRADE_EVENT]: [],
  [SubscriptionIdentifier.MTE_UPLOAD]: [],
  [SubscriptionIdentifier.DEAL_INTEREST_PERIOD]: [],
  [SubscriptionIdentifier.DEAL_HISTORY]: [],
  [SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: [StateFilterId.HIDE_ORG_ACTIVITY],
  [SubscriptionIdentifier.AXE_TODAY_RESPONDER]: [StateFilterId.AXE_SHOW_CANCELLED_RESPONDER],
  [SubscriptionIdentifier.AXE_TODAY_INITIATOR]: [StateFilterId.AXE_SHOW_CANCELLED_INITIATOR],
  [SubscriptionIdentifier.COLLATERAL_DETAILS]: []
}

export interface SubscriptionFilterConfig {
  globalGridSettings: GlobalGridSettings
  authorisedUser: AuthorisedUser
  isBuySide: boolean
}

export const updateWithStateFilters = (
  filters: SubscriptionFilter[],
  subscriptionIdentifier: SubscriptionIdentifier,
  filterConfig: SubscriptionFilterConfig
) => {
  const { globalGridSettings, authorisedUser, isBuySide } = filterConfig

  const validSubscriptionIdentifiers = Object.entries(ValidStateSubscriptionFilterIds)
    .filter(([_identifier, stateFilterIds]) => stateFilterIds.length > 0)
    .map(([identifier, _stateFilterIds]) => identifier)

  if (filters.length === 0 || !validSubscriptionIdentifiers.includes(subscriptionIdentifier)) {
    return filters
  }

  const validStateFilters = ValidStateSubscriptionFilterIds[subscriptionIdentifier]

  const stateFilters = createStateSubscriptionFilters({ authorisedUser })

  let newFilters = filters.map(filter => {
    if (typeof filter.value === 'string') return { ...filter, value: filter.value.trim() }
    return filter
  })

  if (validStateFilters.includes(StateFilterId.AXE_SHOW_CANCELLED_INITIATOR)) {
    if (globalGridSettings.showCancelledAxes) {
      newFilters = newFilters.filter(filter => filter.fieldName !== 'status').concat(stateFilters.AXE_SHOW_CANCELLED_INITIATOR)
    } else if (!globalGridSettings.showCancelledAxes) {
      newFilters = newFilters
        .filter(filter => filter.fieldName !== 'status')
        .concat(DefaultAxeSubscriptionFilters.AXE_TODAY_INITIATOR.filter(f => f.fieldName === 'status'))
    }
  }

  if (validStateFilters.includes(StateFilterId.AXE_SHOW_CANCELLED_RESPONDER)) {
    if (globalGridSettings.showCancelledAxes) {
      newFilters = newFilters.filter(filter => filter.fieldName !== 'status').concat(stateFilters.AXE_SHOW_CANCELLED_RESPONDER)
    } else if (!globalGridSettings.showCancelledAxes) {
      newFilters = newFilters
        .filter(filter => filter.fieldName !== 'status')
        .concat(DefaultAxeSubscriptionFilters.AXE_TODAY_RESPONDER.filter(f => f.fieldName === 'status'))
    }
  }

  if (
    validStateFilters.includes(StateFilterId.HIDE_ORG_ACTIVITY) &&
    authorisedUser.application === Application.TRADING_GATEWAY &&
    isBuySide
  ) {
    if (!globalGridSettings.showOrgActivity && !filters.some(filter => isEqual(filter, stateFilters.HIDE_ORG_ACTIVITY))) {
      newFilters = [...newFilters, stateFilters.HIDE_ORG_ACTIVITY]
    } else if (globalGridSettings.showOrgActivity && filters.some(filter => isEqual(filter, stateFilters.HIDE_ORG_ACTIVITY))) {
      newFilters = newFilters.filter(subscriptionFilter => !isEqual(subscriptionFilter, stateFilters.HIDE_ORG_ACTIVITY))
    }
  }

  if (validStateFilters.includes(StateFilterId.INITIATED_SHOW_EXPIRED_REJECTED)) {
    const statusFilter = filters.find(filter => filter.fieldName === 'status')
    if (globalGridSettings.hideExpiredRejectedInInitiated && statusFilter && !!statusFilter?.orFilter) {
      newFilters = newFilters.filter(filter => filter.fieldName !== 'status').concat({ ...statusFilter, orFilter: null })
    } else if (!globalGridSettings.hideExpiredRejectedInInitiated && statusFilter && !statusFilter?.orFilter) {
      newFilters = newFilters
        .filter(filter => filter.fieldName !== 'status')
        .concat({ ...statusFilter, orFilter: stateFilters.INITIATED_SHOW_EXPIRED_REJECTED })
    }
  }

  if (
    validStateFilters.includes(StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY) &&
    authorisedUser.application === Application.TRADING_GATEWAY
  ) {
    if (
      globalGridSettings.showOnlyUsersSalesActivity &&
      !filters.some(filter => isEqual(filter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY))
    ) {
      newFilters = [...newFilters, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY]
    } else if (
      !globalGridSettings.showOnlyUsersSalesActivity &&
      filters.some(filter => isEqual(filter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY))
    ) {
      newFilters = newFilters.filter(subscriptionFilter => !isEqual(subscriptionFilter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY))
    }
  }

  if (
    validStateFilters.includes(StateFilterId.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION) &&
    authorisedUser.application === Application.TRADING_GATEWAY
  ) {
    if (
      globalGridSettings.showOnlyUsersSalesActivity &&
      !filters.some(filter => isEqual(filter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION))
    ) {
      newFilters = [...newFilters, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION]
    } else if (
      !globalGridSettings.showOnlyUsersSalesActivity &&
      filters.some(filter => isEqual(filter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION))
    ) {
      newFilters = newFilters.filter(
        subscriptionFilter => !isEqual(subscriptionFilter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY_NEGOTIATION)
      )
    }
  }

  if (validStateFilters.includes(StateFilterId.OPEN_REPO_NULL_END_DATE)) {
    if (!globalGridSettings.showTerminatedRepos && !filters.some(filter => isEqual(filter, stateFilters.OPEN_REPO_NULL_END_DATE))) {
      newFilters = [...newFilters, stateFilters.OPEN_REPO_NULL_END_DATE]
    } else if (globalGridSettings.showTerminatedRepos && filters.some(filter => isEqual(filter, stateFilters.OPEN_REPO_NULL_END_DATE))) {
      newFilters = newFilters.filter(subscriptionFilter => !isEqual(subscriptionFilter, stateFilters.OPEN_REPO_NULL_END_DATE))
    }
  }

  return newFilters
}

// Utils
export const areOnlyDefaultSubscriptionFiltersActive = (
  subscriptionIdentifier: SubscriptionIdentifier,
  activeSubscriptionFilters: SubscriptionFilter[],
  authorisedUser: User
) => {
  if (activeSubscriptionFilters.length === 0) return false

  const stateFilters = createStateSubscriptionFilters({ authorisedUser })

  const nonOptional = (subscriptionFilter: SubscriptionFilter) => {
    return (
      !isMarketTradeDateControlledByPlatform(subscriptionFilter) &&
      !isEqual(subscriptionFilter, stateFilters.OPEN_REPO_NULL_END_DATE) &&
      !isEqual(subscriptionFilter, stateFilters.HIDE_ORG_ACTIVITY) &&
      !isEqual(subscriptionFilter, stateFilters.SHOW_ONLY_USERS_SALES_ACTIVITY) &&
      !isEqual(subscriptionFilter, stateFilters.AXE_SHOW_CANCELLED_INITIATOR) &&
      !isEqual(subscriptionFilter, stateFilters.AXE_SHOW_CANCELLED_RESPONDER)
    )
  }

  switch (subscriptionIdentifier) {
    case SubscriptionIdentifier.AXE_TODAY_INITIATOR:
    case SubscriptionIdentifier.AXE_TODAY_RESPONDER: {
      const axeStateFilter =
        subscriptionIdentifier === SubscriptionIdentifier.AXE_TODAY_INITIATOR
          ? stateFilters.AXE_SHOW_CANCELLED_INITIATOR
          : stateFilters.AXE_SHOW_CANCELLED_RESPONDER

      return (
        isEqual(
          DefaultAxeSubscriptionFilters[subscriptionIdentifier].filter(f => f.fieldName !== 'marketTradeDate'),
          activeSubscriptionFilters.filter(f => f.fieldName !== 'marketTradeDate')
        ) ||
        isEqual(
          DefaultAxeSubscriptionFilters[subscriptionIdentifier]
            .filter(f => f.fieldName !== 'status')
            .filter(f => f.fieldName !== 'marketTradeDate')
            .concat(axeStateFilter),
          activeSubscriptionFilters.filter(f => f.fieldName !== 'marketTradeDate')
        )
      )
    }

    default:
      return isEqual(DefaultSubscriptionFilters[subscriptionIdentifier].filter(nonOptional), activeSubscriptionFilters.filter(nonOptional))
  }
}

const isMarketTradeDateControlledByPlatform = (subscriptionFilter: SubscriptionFilter) => {
  if (
    subscriptionFilter.fieldName === 'marketTradeDate' &&
    subscriptionFilter.calculatedValueType === CalculatedValueType.MARKET_TRADE_DATE_EUROPE &&
    subscriptionFilter.orFilter?.calculatedValueType === CalculatedValueType.MARKET_TRADE_DATE_US
  ) {
    return true
  }

  return false
}
